import React from "react";
import { SidePanel } from "../../../ui";
import { FilterPanel } from "./FilterPanel";
import { WindowStaleNotice } from "./WindowStaleNotice";
import { TracesTable } from "./TracesTable";
import { TraceDetailPane } from "./TraceDetailPane";
import { useTraces } from "../hooks/useTraces";
import { useSelectedTrace } from "../hooks/useSelectedTrace";
import { useTracesFilters } from "../hooks/useTracesFilters";
import type { TraceComment } from "../types";

export function TracesView() {
  // Same rich filter surface as the Insights page, persisted under the Traces
  // namespace so the view is preserved across navigation and a chart drill-down
  // can seed it (#1402).
  const {
    metadataKeys,
    metadataValues,
    loadMetadataValuesForKey,
    filters,
    addFilter,
    removeFilter,
    updateFilter,
    criteriaFilter,
    setCriteriaFilter,
    dateWindow,
    setDateWindow,
    dateRangeFilter,
    isWindowStale,
    refreshWindow,
    scoreFilter,
    setScoreFilter,
    criteria,
    loadingCriteria,
    loadCriteria,
  } = useTracesFilters();

  const {
    traces,
    loading,
    hasPrev,
    hasNext,
    currentPage,
    goNext,
    goPrev,
    updateTraceRating,
    appendTraceComment,
  } = useTraces({ filters, criteriaFilter, dateRangeFilter, scoreFilter });

  const {
    selectedId,
    selectedTrace,
    loading: traceLoading,
    notFound: traceNotFound,
    selectTrace,
    applyRating,
    applyComment,
  } = useSelectedTrace(traces);

  // Mirror optimistic edits onto both the loaded page (useTraces) and a
  // deep-linked trace fetched by id (useSelectedTrace); only the match applies.
  const handleRatingChange = (id: string, rating: boolean) => {
    updateTraceRating(id, rating);
    applyRating(id, rating);
  };
  const handleCommentAdded = (id: string, comment: TraceComment) => {
    appendTraceComment(id, comment);
    applyComment(id, comment);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Filters — same controls as Insights (date, score, criteria, tags). */}
      {isWindowStale && (
        <WindowStaleNotice
          dateWindow={dateWindow}
          showing={dateRangeFilter}
          onRefresh={refreshWindow}
        />
      )}
      <FilterPanel
        metadataKeys={metadataKeys}
        filters={filters}
        onAddFilter={addFilter}
        onRemoveFilter={removeFilter}
        onUpdateFilter={updateFilter}
        metadataValues={metadataValues}
        loadMetadataValuesForKey={loadMetadataValuesForKey}
        criteriaFilter={criteriaFilter}
        criteria={criteria}
        loadingCriteria={loadingCriteria}
        onCriteriaFilterChange={setCriteriaFilter}
        onLoadCriteria={loadCriteria}
        dateWindow={dateWindow}
        onDateWindowChange={setDateWindow}
        scoreFilter={scoreFilter}
        onScoreFilterChange={setScoreFilter}
      />

      {/* Table */}
      <TracesTable
        traces={traces}
        loading={loading}
        selectedId={selectedId}
        onRowClick={(id) => selectTrace(selectedId === id ? null : id)}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <button
          disabled={!hasPrev}
          onClick={goPrev}
          className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="text-sm text-muted-foreground">Page {currentPage}</span>
        <button
          disabled={!hasNext}
          onClick={goNext}
          className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      {/* Detail panel */}
      <SidePanel
        isOpen={!!selectedId}
        onClose={() => selectTrace(null)}
        title="Trace Details"
      >
        {traceNotFound ? (
          <p className="text-sm text-muted-foreground">
            Trace not found. It may have been deleted, or the link points to a
            trace from a different domain.
          </p>
        ) : (
          <TraceDetailPane
            trace={selectedTrace}
            loading={traceLoading}
            onRatingChange={handleRatingChange}
            onCommentAdded={handleCommentAdded}
          />
        )}
      </SidePanel>
    </div>
  );
}
